import { IonCol, IonGrid, IonRouterLink, IonRow } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ActivityStage from '@components/ActivityStage';
import ExpandableSection from '@components/animations/ExpandableSection';
import TertiaryButton from '@components/buttons/TertiaryButton';
import InitialConsultFunctionalForm from '@components/forms/InitialConsultFunctionalForm';
import PersonalizeForm from '@components/forms/PersonalizeForm';
import InitialConsultQuestion from '@components/InitialConsultQuestion';
import Modal from '@components/Modal';
import PMEQuestion from '@components/PMEQuestion';
import styles from '@components/profile/Profile.module.css';
import Icon from '@components/text/Icon';
import LargeText from '@components/text/LargeText';
import Heading2 from '@components/typography/Heading2';
import useOpener from '@hooks/useOpener';
import {
  CareProfileActionableFieldEnum,
  CareProfileCaregiverTypeEnum,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { InitialConsultFields, PersonalizeExperienceFields } from '@typing/Models';
type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const Functional = ({ journey }: Props) => {
  const opener = useOpener();
  const intl = useIntl();

  const [editingQuestion, setEditingQuestion] = useState<PersonalizeExperienceFields[]>([]);
  const [editingInitialConsult, setEditingInitialConsult] = useState<InitialConsultFields[] | undefined>(undefined);

  const initialConsultOpener = useOpener();
  const personalizeFunctionalOpener = useOpener();

  const handlePMEQuestionChange = useCallback(
    (field: PersonalizeExperienceFields) => () => {
      setEditingQuestion([field]);
      personalizeFunctionalOpener.open();
    },
    [personalizeFunctionalOpener]
  );

  const handleInitialConsultQuestionChange = useCallback(
    (field: InitialConsultFields) => () => {
      setEditingInitialConsult([field]);
      initialConsultOpener.open();
    },
    [initialConsultOpener]
  );

  const residenceType = useMemo(() => {
    if (journey.careProfile.residenceType?.other) {
      return journey.careProfile.residenceType.other;
    }
    if (journey.careProfile.residenceType?.residenceTypeOption) {
      return intl.formatMessage({
        id: `models.pme.questions.residence_type.options.${journey.careProfile.residenceType.residenceTypeOption}.content`
      });
    }
    return null;
  }, [journey.careProfile.residenceType, intl]);

  const currentCareTransitions = useMemo(() => {
    const careTransitions = journey.careProfile.currentCareTransitions?.currentCareTransitionsOptions.map(option =>
      intl.formatMessage({
        id: `models.pme.questions.current_care_transitions.options.${option}.content`
      })
    );

    if (journey.careProfile.currentCareTransitions?.other) {
      careTransitions?.push(journey.careProfile.currentCareTransitions?.other);
    }
    return careTransitions ?? [];
  }, [journey.careProfile.currentCareTransitions, intl]);

  return (
    <div>
      <IonRouterLink onClick={opener.toggle}>
        <div className={styles.section}>
          <Heading2>
            <FormattedMessage id="pages.client.careProfile.sections.functional" />
          </Heading2>
          <Icon
            ariaLabelKey="accessibility.ariaLabels.toggle"
            className={styles.toggleOpener}
            icon={opener.isOpen ? 'chevronUp' : 'chevronDown'}
          />
        </div>
      </IonRouterLink>
      <ExpandableSection isOpen={opener.isOpen}>
        <PMEQuestion
          field={CareProfileActionableFieldEnum.help_level_required}
          handleEdit={handlePMEQuestionChange}
          selections={[
            journey.careProfile.helpLevelRequired
              ? intl.formatMessage({
                  id: `models.pme.questions.help_level_required.options.${journey.careProfile.helpLevelRequired}.content`
                })
              : null
          ]}
        />
        <PMEQuestion
          field={CareProfileActionableFieldEnum.residence_type}
          handleEdit={handlePMEQuestionChange}
          selections={[residenceType]}
        />
        <PMEQuestion
          field={CareProfileActionableFieldEnum.current_care_transitions}
          handleEdit={handlePMEQuestionChange}
          selections={currentCareTransitions}
        />
        <InitialConsultQuestion
          field={InitialConsultFields.CONSIDERING_HOUSING_CHANGE}
          handleEdit={handleInitialConsultQuestionChange}
          selections={[
            journey.careProfile.consideringHousingChange
              ? intl.formatMessage({ id: 'dictionary.yes' })
              : journey.careProfile.consideringHousingChange === false
              ? intl.formatMessage({ id: 'dictionary.no' })
              : null
          ]}
          titleKey="models.careProfile.questions.considering_housing_change.content"
        />
        <InitialConsultQuestion
          field={InitialConsultFields.CAREGIVER_TYPES}
          handleEdit={handleInitialConsultQuestionChange}
          selections={
            journey.careProfile.caregiverTypes?.caregiverTypesOptions?.map(option => {
              if (option === CareProfileCaregiverTypeEnum.other && journey.careProfile.caregiverTypes?.other) {
                return journey.careProfile.caregiverTypes?.other;
              }
              return intl.formatMessage({
                id: `models.initialConsultReport.questions.caregiver_types.options.${option}.content`
              });
            }) ?? []
          }
          titleKey="models.initialConsultReport.questions.caregiver_types.content"
        />
        <div className="ion-margin-bottom">
          <IonGrid className="ion-no-padding">
            <IonRow>
              <IonCol>
                <LargeText>
                  <FormattedMessage id="models.initialConsultReport.questions.stages.content" />
                </LargeText>
              </IonCol>
              <IonCol size="auto">
                <TertiaryButton
                  icon="edit"
                  onClick={handleInitialConsultQuestionChange(InitialConsultFields.STAGES)}
                  textKey="dictionary.edit"
                />
              </IonCol>
            </IonRow>
          </IonGrid>
          <ActivityStage stage={journey.careProfile.stageBathingAndDressing} type="bathing_and_dressing" />
          <ActivityStage stage={journey.careProfile.stageContinence} type="continence" />
          <ActivityStage stage={journey.careProfile.stageEating} type="eating" />
          <ActivityStage stage={journey.careProfile.stageHousekeeping} type="housekeeping" />
          <ActivityStage stage={journey.careProfile.stageMealPrep} type="meal_prep" />
          <ActivityStage stage={journey.careProfile.stageShopping} type="shopping" />
          <ActivityStage stage={journey.careProfile.stageToileting} type="toileting" />
          <ActivityStage stage={journey.careProfile.stageTransportation} type="transportation" />
        </div>
      </ExpandableSection>
      <Modal
        opener={personalizeFunctionalOpener}
        title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}
      >
        {() => (
          <PersonalizeForm
            careProfile={journey.careProfile}
            clientRole={null}
            onCancel={personalizeFunctionalOpener.close}
            onSuccess={personalizeFunctionalOpener.close}
            questions={editingQuestion}
          />
        )}
      </Modal>
      <Modal opener={initialConsultOpener} title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}>
        {() => (
          <InitialConsultFunctionalForm
            careProfile={journey.careProfile}
            consultId={journey.initialConsultReport.consult?.id ?? null}
            initialConsultReportId={journey.initialConsultReport.id}
            onCancel={initialConsultOpener.close}
            onSuccess={initialConsultOpener.close}
            questions={editingInitialConsult}
          />
        )}
      </Modal>
    </div>
  );
};

export default Functional;
